import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutGrid, Code2, Atom, Sparkles, Bookmark } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useEditor } from "./useEditorState";
import MyBlocks from "./MyBlocks";
import { errMsg } from "@/lib/api";

const TABS = [["library", "Libreria", LayoutGrid], ["mine", "Le mie sezioni", Bookmark], ["html", "Codice HTML", Code2], ["react", "React", Atom], ["ai", "Genera con AI", Sparkles]];

function Library({ lib, onAdd }) {
  const cats = [...new Set(lib.blocks.map((b) => b.category))];
  return (
    <div className="space-y-5 max-h-[55vh] overflow-auto pr-1">
      {cats.map((c) => (
        <div key={c}>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">{c}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {lib.blocks.filter((b) => b.category === c).map((b) => (
              <button key={b.key} onClick={() => onAdd({ name: b.name, type: "react", code: b.code, props: structuredClone(b.props), origin: "predefinito" })}
                className="text-left rounded-lg border p-3 hover:border-sky-400 hover:bg-sky-50/40 transition-colors" data-testid={`block-${b.key}`}>
                <p className="text-sm font-medium text-slate-900">{b.name}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{b.category.startsWith("Prenotazioni") ? "Widget reale: servizi, orari e modulo dal pannello Proprietà" : Object.keys(b.props).slice(0, 4).join(" · ")}</p>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AddSectionDialog() {
  const ed = useEditor();
  const target = ed.ui.addSection;
  const [tab, setTab] = useState("library");
  const [lib, setLib] = useState(null);
  const [h, setH] = useState({ name: "Sezione personalizzata", html: "", css: "", js: "" });
  const [prompt, setPrompt] = useState("");
  useEffect(() => { if (target && !lib) ed.api.get("/library/blocks").then(({ data }) => setLib(data)); }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!target) return null;
  const close = () => ed.setUi({ addSection: null });
  const add = (section) => {
    const sid = ed.addSection(target.pageId, section, target.index);
    ed.setSelection({ kind: "section", id: sid });
    ed.setUi({ addSection: null, rightTab: section.type === "html" || section.origin === "custom" ? "code" : "props", leftTab: "sections" });
    ed.goToPage?.(target.pageId);
    toast.success("Sezione aggiunta");
    if (/bookingApi/.test(section.code || "")) ed.save().then(() => window.dispatchEvent(new Event("adm:project-changed")));
  };
  const askAI = async () => {
    const page = ed.content.pages.find((p) => p.id === target.pageId);
    const after = page.sections[target.index - 1];
    const msg = `Aggiungi una nuova sezione nella pagina "${page.name}" ${after ? `subito dopo la sezione "${after.name}" (id ${after.id})` : "all'inizio"}: ${prompt}. Non modificare le altre sezioni.`;
    try {
      await ed.save();
      await ed.api.post(`/projects/${ed.id}/chat`, { message: msg, scope: { type: "page", page_id: page.id }, auto_apply: false });
      ed.setUi({ addSection: null, rightTab: "ai", rightOpen: true }); setPrompt("");
      ed.setSelection(null);
      toast.message("Richiesta inviata all'AI: troverai la proposta nella chat");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-2xl" data-testid="add-section-dialog">
        <DialogHeader><DialogTitle>Aggiungi sezione</DialogTitle><DialogDescription>Scegli un blocco predefinito, incolla codice esterno, scrivi un componente React o chiedi all'AI.</DialogDescription></DialogHeader>
        <div className="flex flex-wrap gap-x-1 border-b min-w-0" data-testid="add-section-tabs">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`add-section-tab-${k}`}
              className={`whitespace-nowrap px-3 py-2 text-xs inline-flex items-center gap-1.5 border-b-2 ${tab === k ? "border-sky-600 text-slate-900 font-medium" : "border-transparent text-slate-500"}`}><Icon size={13} />{l}</button>
          ))}
        </div>
        {tab === "library" && (lib ? <Library lib={lib} onAdd={add} /> : <p className="text-sm text-slate-500">Caricamento…</p>)}
        {tab === "mine" && <MyBlocks onAdd={add} />}
        {tab === "html" && (
          <div className="space-y-2.5">
            <Input value={h.name} onChange={(e) => setH({ ...h, name: e.target.value })} placeholder="Nome sezione" data-testid="custom-section-name-input" />
            <Textarea rows={7} value={h.html} onChange={(e) => setH({ ...h, html: e.target.value })} placeholder="Incolla qui l'HTML (anche con <style> e <script>)" className="font-mono text-xs" data-testid="custom-html-input" />
            <div className="grid sm:grid-cols-2 gap-2.5">
              <Textarea rows={4} value={h.css} onChange={(e) => setH({ ...h, css: e.target.value })} placeholder="CSS (facoltativo)" className="font-mono text-xs" data-testid="custom-css-input" />
              <Textarea rows={4} value={h.js} onChange={(e) => setH({ ...h, js: e.target.value })} placeholder="JavaScript (facoltativo)" className="font-mono text-xs" data-testid="custom-js-input" />
            </div>
            <div className="flex justify-between gap-2">
              <Button variant="ghost" size="sm" onClick={() => lib && setH({ ...h, ...lib.html_starter })}>Usa esempio</Button>
              <Button onClick={() => add({ ...h, type: "html", origin: "custom" })} disabled={!h.html.trim()} data-testid="custom-html-add-btn">Aggiungi codice personalizzato</Button>
            </div>
          </div>
        )}
        {tab === "react" && (
          <div className="space-y-3 text-sm text-slate-600">
            <p>Crea una sezione React con codice di partenza, oppure incolla un componente creato con ChatGPT: puoi usare <code className="text-xs bg-slate-100 px-1 rounded">react</code>, <code className="text-xs bg-slate-100 px-1 rounded">lucide-react</code> e gli helper <code className="text-xs bg-slate-100 px-1 rounded">demo</code> (Link, useStore, DATA…).</p>
            <Button onClick={() => add({ name: "Nuova sezione React", type: "react", code: lib?.react_starter || "export default function Sezione() { return <section className=\"p-10\">Nuova sezione</section>; }", origin: "custom" })} data-testid="react-section-add-btn">Crea sezione React</Button>
          </div>
        )}
        {tab === "ai" && (
          <div className="space-y-2.5">
            <Textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Es. Una sezione con tre pacchetti benessere, card eleganti e pulsante Prenota" data-testid="ai-section-prompt-input" />
            <p className="text-[11px] text-slate-500">L'AI creerà solo la nuova sezione; potrai rivederla prima di applicarla.</p>
            <div className="flex justify-end"><Button onClick={askAI} disabled={!prompt.trim()} className="bg-sky-600 hover:bg-sky-700" data-testid="ai-section-generate-btn"><Sparkles size={14} className="mr-1.5" />Genera sezione</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
